import React from 'react';
import { Line, Bar, Scatter } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface EducationVisualsProps {
  type: 'graph' | 'plot' | 'chart' | 'diagram';
  data: any;
  className?: string;
}

const EducationVisuals: React.FC<EducationVisualsProps> = ({ type, data, className = '' }) => {
  // Common chart options
  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 1000,
      easing: 'easeInOutQuart'
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: 'rgba(255, 255, 255, 1)',
          font: { size: 12 }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#7e22ce',
        bodyColor: '#7e22ce',
        padding: 12,
        cornerRadius: 8,
        boxPadding: 6
      }
    },
    scales: {
      x: {
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)'
        }
      },
      y: {
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)'
        }
      }
    }
  };

  const renderGraph = () => {
    return (
      <Line
        data={{
          labels: data.labels,
          datasets: data.datasets.map((dataset: any) => ({
            ...dataset,
            borderColor: dataset.color || 'rgba(147, 51, 234, 0.8)',
            backgroundColor: dataset.fill ? `${dataset.color}20` || 'rgba(147, 51, 234, 0.2)' : undefined,
            tension: 0.4
          }))
        }}
        options={commonOptions}
      />
    );
  };

  const renderPlot = () => {
    return (
      <Scatter
        data={{
          datasets: data.datasets.map((dataset: any) => ({
            ...dataset,
            backgroundColor: dataset.color || 'rgba(147, 51, 234, 0.8)'
          }))
        }}
        options={commonOptions}
      />
    );
  };

  const renderChart = () => {
    return (
      <Bar
        data={{
          labels: data.labels,
          datasets: data.datasets.map((dataset: any) => ({
            ...dataset,
            backgroundColor: dataset.color || 'rgba(147, 51, 234, 0.8)'
          }))
        }}
        options={commonOptions}
      />
    );
  };

  const renderDiagram = () => {
    // For custom diagrams (e.g., geometry shapes, science diagrams)
    return (
      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-white">
        <svg
          viewBox="0 0 400 300"
          className="w-full h-full"
          style={{ maxHeight: '300px' }}
        >
          {data.elements.map((element: any, index: number) => {
            switch (element.type) {
              case 'circle':
                return (
                  <circle
                    key={index}
                    cx={element.cx}
                    cy={element.cy}
                    r={element.r}
                    fill={element.fill || 'none'}
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                );
              case 'rect':
                return (
                  <rect
                    key={index}
                    x={element.x}
                    y={element.y}
                    width={element.width}
                    height={element.height}
                    fill={element.fill || 'none'}
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                );
              case 'line':
                return (
                  <line
                    key={index}
                    x1={element.x1}
                    y1={element.y1}
                    x2={element.x2}
                    y2={element.y2}
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                );
              case 'text':
                return (
                  <text
                    key={index}
                    x={element.x}
                    y={element.y}
                    fill="currentColor"
                    fontSize={element.fontSize || 14}
                    textAnchor={element.anchor || 'middle'}
                  >
                    {element.content}
                  </text>
                );
              default:
                return null;
            }
          })}
        </svg>
      </div>
    );
  };

  return (
    <div className={`w-full h-[300px] bg-purple-800/20 backdrop-blur-sm rounded-lg p-4 ${className}`}>
      {type === 'graph' && renderGraph()}
      {type === 'plot' && renderPlot()}
      {type === 'chart' && renderChart()}
      {type === 'diagram' && renderDiagram()}
    </div>
  );
};

export default EducationVisuals;